import React, { useEffect, useState } from 'react'
import useSWR from 'swr'
import {
  Box,
  Button,
  Checkbox,
  Divider,
  Group,
  Loader,
  ScrollArea,
  Select,
  Stack,
  TextInput,
} from '@mantine/core'
import { useForm } from '@mantine/form'
import { notifications } from '@mantine/notifications'
import Client from '../../models/client'
import { createShop, fetchShop, fetchShopsOptions, updateShop } from '../api'
import { BaseModal, requireLabelStyles, requiredMark } from './BaseModal'
import { PREFECTURE_LIST } from './Global'
import { ShopsOption } from './ShopsOptionModal'
import { confirmDiscardChanges } from '../utils/confirm'

interface Props {
  opened: boolean
  onClose: () => void
  client: Client
  shopId: number | null
  onSaved?: () => void
}

interface FormValues {
  code: string
  zip_code: string
  prefecture_id: string
  address1: string
  address2: string
  address3: string
  name: string
  custom_name: string
  kana: string
  kana_eng: string
  category1: string
  category2: string
  category3: string
  invite_hidden: boolean
  guest_hidden: boolean
  coming_hidden: boolean
  select_option1: string
  select_option2: string
  select_option3: string
  select_option4: string
  select_option5: string
  notification_email: string
  shop_page_url: string
}

const defaultValues: FormValues = {
  code: '',
  zip_code: '',
  prefecture_id: '',
  address1: '',
  address2: '',
  address3: '',
  name: '',
  custom_name: '',
  kana: '',
  kana_eng: '',
  category1: '',
  category2: '',
  category3: '',
  invite_hidden: false,
  guest_hidden: false,
  coming_hidden: false,
  select_option1: '',
  select_option2: '',
  select_option3: '',
  select_option4: '',
  select_option5: '',
  notification_email: '',
  shop_page_url: '',
}

const PREFECTURE_SELECT_DATA = PREFECTURE_LIST.map((p) => ({
  value: String(p.id),
  label: p.name,
}))

export const ShopFormModal: React.FC<Props> = ({
  opened,
  onClose,
  client,
  shopId,
  onSaved,
}) => {
  const isNewShop = shopId === null
  const [submitting, setSubmitting] = useState(false)

  const { data: shop, isLoading } = useSWR(
    opened && !isNewShop ? `/admin/clients/${client.id}/shops/${shopId}` : null,
    () => (shopId !== null ? fetchShop(client.id, shopId) : null),
  )

  const { data: shopsOptions } = useSWR<ShopsOption[]>(
    opened ? `/admin/clients/${client.id}/shop_options` : null,
    () => fetchShopsOptions(client.id),
  )

  const optionLabel = (optionKey: string, defaultLabel: string): string => {
    const name = shopsOptions?.find((o) => o.option_key === optionKey)?.option_name
    return name ? `${defaultLabel}（${name}）` : defaultLabel
  }

  const form = useForm<FormValues>({
    initialValues: defaultValues,
    validate: {
      prefecture_id: (v) => (!v ? '必須です' : null),
      name: (v) => (!v ? '必須です' : null),
    },
  })

  useEffect(() => {
    if (!opened) return
    if (isNewShop) {
      form.setValues(defaultValues)
      form.resetDirty()
      return
    }
    if (shop) {
      form.setValues({
        code: shop.code ?? '',
        zip_code: shop.zip_code ?? '',
        prefecture_id: shop.prefecture_id ? String(shop.prefecture_id) : '',
        address1: shop.address1 ?? '',
        address2: shop.address2 ?? '',
        address3: shop.address3 ?? '',
        name: shop.name ?? '',
        custom_name: shop.custom_name ?? '',
        kana: shop.kana ?? '',
        kana_eng: shop.kana_eng ?? '',
        category1: shop.category1 ?? '',
        category2: shop.category2 ?? '',
        category3: shop.category3 ?? '',
        invite_hidden: !!shop.invite_hidden,
        guest_hidden: !!shop.guest_hidden,
        coming_hidden: !!shop.coming_hidden,
        select_option1: shop.select_option1 ?? '',
        select_option2: shop.select_option2 ?? '',
        select_option3: shop.select_option3 ?? '',
        select_option4: shop.select_option4 ?? '',
        select_option5: shop.select_option5 ?? '',
        notification_email: shop.notification_email ?? '',
        shop_page_url: shop.shop_page_url ?? '',
      })
      form.resetDirty()
    }
  }, [opened, shop, isNewShop])

  const handleClose = () => {
    if (form.isDirty() && !confirmDiscardChanges()) {
      return
    }
    onClose()
  }

  const handleSubmit = async (values: FormValues) => {
    setSubmitting(true)
    try {
      const payload = {
        ...values,
        prefecture_id: Number(values.prefecture_id),
      }
      if (isNewShop) {
        await createShop(client.id, payload)
        notifications.show({ message: '店舗を作成しました', color: 'teal' })
      } else if (shopId !== null) {
        await updateShop(client.id, shopId, payload)
        notifications.show({ message: '店舗を更新しました', color: 'teal' })
      }
      onSaved?.()
      onClose()
    } catch (e) {
      console.error(isNewShop ? '店舗作成に失敗しました' : '店舗更新に失敗しました', e)
      notifications.show({
        message: isNewShop ? '店舗作成に失敗しました' : '店舗更新に失敗しました',
        color: 'red',
      })
    } finally {
      setSubmitting(false)
    }
  }

  const showLoader = !isNewShop && (isLoading || !shop)

  return (
    <BaseModal
      opened={opened}
      onClose={handleClose}
      title={isNewShop ? '新規店舗作成' : '店舗編集'}
      onExited={() => {
        form.setValues(defaultValues)
        form.resetDirty()
      }}
    >
      {showLoader ? (
        <Group justify='center' py='xl' style={{ width: '100%' }}>
          <Loader />
        </Group>
      ) : (
        <form
          onSubmit={form.onSubmit(handleSubmit)}
          onKeyDown={(e) => {
            if (e.key === 'Enter' && (e.target as HTMLElement).tagName !== 'TEXTAREA') {
              e.preventDefault()
            }
          }}
          style={{
            display: 'flex',
            flexDirection: 'column',
            flex: 1,
            minHeight: 0,
          }}
        >
          <ScrollArea style={{ flex: 1, minHeight: 0 }} bg='#00000008' px={24}>
            <Stack gap='md' p='md'>
              <Divider label='基本情報' labelPosition='left' />
              <TextInput label='店舗コード' {...form.getInputProps('code')} />
              <TextInput label='郵便番号' {...form.getInputProps('zip_code')} />
              <Select
                label={<><span>都道府県</span>{requiredMark}</>}
                data={PREFECTURE_SELECT_DATA}
                {...form.getInputProps('prefecture_id')}
                allowDeselect={false}
                styles={requireLabelStyles}
              />
              <TextInput label='住所1' {...form.getInputProps('address1')} />
              <TextInput label='住所2' {...form.getInputProps('address2')} />
              <TextInput label='住所3' {...form.getInputProps('address3')} />
              <TextInput
                label={<><span>店舗名</span>{requiredMark}</>}
                {...form.getInputProps('name')}
                styles={requireLabelStyles}
              />
              <TextInput label='店舗名（表示名）' {...form.getInputProps('custom_name')} />
              <TextInput label='店舗名カナ' {...form.getInputProps('kana')} />
              <TextInput label='店舗名英語' {...form.getInputProps('kana_eng')} />

              <Divider label='カテゴリー' labelPosition='left' />
              <TextInput label='カテゴリー1' {...form.getInputProps('category1')} />
              <TextInput label='カテゴリー2' {...form.getInputProps('category2')} />
              <TextInput label='カテゴリー3' {...form.getInputProps('category3')} />

              <Divider label='表示設定' labelPosition='left' />
              <Checkbox
                label='招待フォームで非表示'
                {...form.getInputProps('invite_hidden', { type: 'checkbox' })}
              />
              <Checkbox
                label='ゲストフォームで非表示'
                {...form.getInputProps('guest_hidden', { type: 'checkbox' })}
              />
              <Checkbox
                label='来店フォームで非表示'
                {...form.getInputProps('coming_hidden', { type: 'checkbox' })}
              />

              <Divider label='選択肢設定' labelPosition='left' />
              <TextInput label={optionLabel('select_option1', '選択肢1')} {...form.getInputProps('select_option1')} />
              <TextInput label={optionLabel('select_option2', '選択肢2')} {...form.getInputProps('select_option2')} />
              <TextInput label={optionLabel('select_option3', '選択肢3')} {...form.getInputProps('select_option3')} />
              <TextInput label={optionLabel('select_option4', '選択肢4')} {...form.getInputProps('select_option4')} />
              <TextInput label={optionLabel('select_option5', '選択肢5')} {...form.getInputProps('select_option5')} />

              <Divider label='その他' labelPosition='left' />
              <TextInput
                type='email'
                label='通知用メールアドレス'
                {...form.getInputProps('notification_email')}
              />
              <TextInput
                type='url'
                label='店舗ページURL'
                {...form.getInputProps('shop_page_url')}
              />
            </Stack>
          </ScrollArea>

          <Box
            p='md'
            style={{
              borderTop: '1px solid #0000001A',
              backgroundColor: '#fff',
              flexShrink: 0,
            }}
          >
            <Group justify='flex-end' gap={8}>
              <Button
                variant='outline'
                radius='xl'
                color='gray'
                onClick={handleClose}
                disabled={submitting}
              >
                キャンセル
              </Button>
              <Button
                variant='filled'
                radius='xl'
                type='submit'
                loading={submitting}
              >
                保存
              </Button>
            </Group>
          </Box>
        </form>
      )}
    </BaseModal>
  )
}
